import { DetectiveHireStatus } from "@/constants/enum/enum";
import { DETECTIVE_HIRING_TIME } from "@/constants/detective";
import type { Detective } from "../pages/DetectiveAgencyPage/types";

export type DetectiveSidebarBadge = "FOUND" | "SEARCHING" | null;

const isDetectiveReadyToReveal = (
  detective: Detective,
  currentTime: number
): boolean =>
  detective.rawStatus === DetectiveHireStatus.Success &&
  !detective.isTargetRevealed &&
  detective.finishedTimestamp !== null &&
  detective.finishedTimestamp > 0 &&
  detective.targetNumber !== null &&
  detective.targetNumber > 0 &&
  currentTime >= detective.finishedTimestamp + detective.targetNumber * 60;

/** Whether any successful detective hire is ready for the player to reveal. */
export const hasDetectiveReadyToReveal = (
  detectives: readonly Detective[],
  currentTime: number
): boolean =>
  detectives.some((detective) => isDetectiveReadyToReveal(detective, currentTime));

/** Show the status of the most recently hired detectives. */
export const getDetectiveSidebarBadge = (
  detectives: readonly Detective[],
  currentTime: number
): DetectiveSidebarBadge => {
  // Compare every hire by the same key so API ordering cannot change the result.
  const allHaveRequestTime = detectives.every(
    (detective) => Number.isFinite(Number(detective.requestedTimestamp)) &&
      Number(detective.requestedTimestamp) > 0
  );
  const latest = detectives.reduce<Detective | null>((newest, detective) => {
    if (!newest) return detective;
    if (allHaveRequestTime) {
      const requestedAt = Number(detective.requestedTimestamp);
      const newestRequestedAt = Number(newest.requestedTimestamp);
      if (requestedAt !== newestRequestedAt) {
        return requestedAt > newestRequestedAt ? detective : newest;
      }
    }
    const id = Number(detective.id) || 0;
    const newestId = Number(newest.id) || 0;
    if (id !== newestId) return id > newestId ? detective : newest;
    return (Number(detective.requestedTimestamp) || 0) >
      (Number(newest.requestedTimestamp) || 0)
      ? detective
      : newest;
  }, null);

  if (!latest || latest.isTargetRevealed) return null;
  if (isDetectiveReadyToReveal(latest, currentTime)) return "FOUND";

  if (latest.rawStatus === DetectiveHireStatus.Pending) return "SEARCHING";

  if (latest.rawStatus === DetectiveHireStatus.Success) {
    return "SEARCHING";
  }

  if (latest.rawStatus === DetectiveHireStatus.Failed) {
    const stillSearching = latest.finishedTimestamp === null ||
      latest.finishedTimestamp <= 0 ||
      currentTime < latest.finishedTimestamp + DETECTIVE_HIRING_TIME;
    return stillSearching ? "SEARCHING" : null;
  }

  return null;
};
